/**
 * อ่านสต๊อกคงเหลือและความเคลื่อนไหวสต๊อก — `GET /v1/stock-balances`, `GET /v1/inventory-ledger` (D-114)
 *
 * อ่านอย่างเดียว ไม่มีเส้นทางเขียนใดเลย ปริมาณ (`qty`) เป็น string ตามที่เซิร์ฟเวอร์ส่ง ห้ามแปลงเป็น number
 * (NUMERIC(18,4)) และมีค่าติดลบได้ซึ่งเป็นคำตอบจริง ไม่ใช่ความผิดพลาด ไม่มี "available" — เซิร์ฟเวอร์ไม่ตัดสินแทน
 */

import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const STOCK_BALANCES_PATH = '/v1/stock-balances';
export const INVENTORY_LEDGER_PATH = '/v1/inventory-ledger';

export interface StockBalance {
  readonly plant_code: string;
  readonly storage_location_code: string;
  readonly product_code: string;
  readonly lot_id: string;
  readonly quality_status: string;
  readonly qty: string;
}

export interface LedgerMovement {
  readonly ledger_id: string;
  readonly plant_code: string;
  readonly storage_location_code: string;
  readonly product_code: string;
  readonly lot_id: string;
  readonly movement_type: string;
  readonly qty: string;
  readonly reference_doc_type: string;
  readonly reference_doc_id: string;
  readonly created_at: string;
}

export interface Page<T> {
  readonly items: readonly T[];
  readonly next_cursor: string | null;
}

export type StockFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden' }
  | {
      readonly status: 'rejected';
      readonly httpStatus: number;
      readonly rule: string | null;
      readonly message: string | null;
      /** `details` ของ error envelope (เช่น `work_center_codes`) — มีเฉพาะเมื่อเซิร์ฟเวอร์ส่งมา */
      readonly details?: Readonly<Record<string, unknown>>;
    }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' };

export type StockResult<T> = { readonly status: 'ready'; readonly page: Page<T> } | StockFailure;

export interface StockRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

export interface StockBalanceQuery {
  readonly plant_code: string;
  readonly storage_location_code?: string;
  readonly product_code?: string;
  readonly include_zero?: boolean;
  readonly limit?: number;
  readonly cursor?: string;
}

export interface LedgerQuery {
  readonly plant_code: string;
  readonly date_from: string;
  readonly date_to: string;
  readonly storage_location_code?: string;
  readonly product_code?: string;
  readonly movement_type?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export function buildQuery(query: Readonly<Record<string, string | number | boolean | undefined>>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || (typeof value === 'string' && value.trim() === '')) {
      continue;
    }
    params.append(key, typeof value === 'string' ? value.trim() : String(value));
  }
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function parseStockBalance(raw: unknown): StockBalance | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const plant = str(r.plant_code);
  const location = str(r.storage_location_code);
  const product = str(r.product_code);
  const lot = str(r.lot_id);
  const quality = str(r.quality_status);
  const qty = str(r.qty);
  if (!plant || !location || !product || !lot || !quality || !qty) {
    return null;
  }
  return {
    plant_code: plant,
    storage_location_code: location,
    product_code: product,
    lot_id: lot,
    quality_status: quality,
    qty,
  };
}

export function parseLedgerMovement(raw: unknown): LedgerMovement | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const fields = [
    'ledger_id',
    'plant_code',
    'storage_location_code',
    'product_code',
    'lot_id',
    'movement_type',
    'qty',
    'reference_doc_type',
    'reference_doc_id',
    'created_at',
  ] as const;
  const out: Record<string, string> = {};
  for (const field of fields) {
    const value = str(r[field]);
    if (value === null) {
      return null;
    }
    out[field] = value;
  }
  return out as unknown as LedgerMovement;
}

function parsePage<T>(raw: unknown, parseItem: (item: unknown) => T | null): Page<T> | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as { items?: unknown; next_cursor?: unknown };
  if (!Array.isArray(r.items)) {
    return null;
  }
  const items: T[] = [];
  for (const entry of r.items) {
    const parsed = parseItem(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  const cursor = r.next_cursor;
  if (cursor !== null && cursor !== undefined && typeof cursor !== 'string') {
    return null;
  }
  return { items, next_cursor: typeof cursor === 'string' && cursor !== '' ? cursor : null };
}

async function read<T>(
  path: string,
  parseItem: (item: unknown) => T | null,
  options: StockRequestOptions,
): Promise<StockResult<T>> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  const url = buildApiUrl(options.apiBaseUrl ?? getConfiguredApiBaseUrl(), path);
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    return { status: 'network-error', detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ' };
  }
  if (response.status === 401) {
    return { status: 'unauthenticated' };
  }
  if (response.status === 403) {
    return { status: 'forbidden' };
  }
  if (response.status >= 400 && response.status < 500) {
    let rule: string | null = null;
    let message: string | null = null;
    try {
      const body = (await response.json()) as { message?: unknown; details?: { rule?: unknown } };
      message = typeof body.message === 'string' ? body.message : null;
      rule = typeof body.details?.rule === 'string' ? body.details.rule : null;
    } catch {
      /* ไม่มี body ที่อ่านได้ */
    }
    return { status: 'rejected', httpStatus: response.status, rule, message };
  }
  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { status: 'invalid-response' };
  }
  const page = parsePage(payload, parseItem);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/stock-balances` — `plant_code` บังคับ */
export function fetchStockBalances(
  query: StockBalanceQuery,
  options: StockRequestOptions = {},
): Promise<StockResult<StockBalance>> {
  return read(`${STOCK_BALANCES_PATH}${buildQuery({ ...query })}`, parseStockBalance, options);
}

/** `GET /v1/inventory-ledger` — `plant_code`, `date_from`, `date_to` บังคับ (ช่วงไม่เกิน 92 วัน) */
export function fetchInventoryLedger(
  query: LedgerQuery,
  options: StockRequestOptions = {},
): Promise<StockResult<LedgerMovement>> {
  return read(`${INVENTORY_LEDGER_PATH}${buildQuery({ ...query })}`, parseLedgerMovement, options);
}

export function stockFailureText(failure: StockFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่';
    case 'forbidden':
      return 'ไม่มีสิทธิ์ดูโรงงานนี้';
    case 'rejected':
      return `${failure.message ?? 'เซิร์ฟเวอร์ปฏิเสธคำขอ'}${failure.rule ? ` (${failure.rule})` : ''}`;
    case 'server-error':
      return `เซิร์ฟเวอร์ผิดพลาด (${failure.httpStatus})`;
    case 'network-error':
      return `เชื่อมต่อไม่ได้: ${failure.detail}`;
    case 'invalid-response':
      return 'คำตอบจากเซิร์ฟเวอร์ผิดรูป';
  }
}

/* ------------------------------------------------- เขียน/อ่านรายการเดียว -- */

export type CallResult =
  | { readonly status: 'ok'; readonly httpStatus: number; readonly payload: unknown }
  | StockFailure;

/** ยิงคำขอ JSON หนึ่งครั้ง (ใช้ร่วมกับหน้านำเข้าสต๊อกตั้งต้นและ staging) — ไม่ตีความ payload */
export async function callJson(
  method: 'GET' | 'POST' | 'PATCH',
  path: string,
  body: unknown,
  headers: Readonly<Record<string, string>>,
  options: StockRequestOptions = {},
): Promise<CallResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  const url = buildApiUrl(options.apiBaseUrl ?? getConfiguredApiBaseUrl(), path);
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    return { status: 'network-error', detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ' };
  }
  if (response.status === 401) {
    return { status: 'unauthenticated' };
  }
  if (response.status === 403) {
    return { status: 'forbidden' };
  }
  if (response.status >= 400 && response.status < 500) {
    let rule: string | null = null;
    let message: string | null = null;
    let details: Record<string, unknown> | null = null;
    try {
      const parsed = (await response.json()) as { message?: unknown; details?: Record<string, unknown> };
      message = typeof parsed.message === 'string' ? parsed.message : null;
      rule = typeof parsed.details?.rule === 'string' ? parsed.details.rule : null;
      details = typeof parsed.details === 'object' && parsed.details !== null ? parsed.details : null;
    } catch {
      /* ไม่มี body ที่อ่านได้ */
    }
    return {
      status: 'rejected',
      httpStatus: response.status,
      rule,
      message,
      ...(details === null ? {} : { details }),
    };
  }
  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }
  try {
    return { status: 'ok', httpStatus: response.status, payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** คีย์ idempotency จากแหล่งสุ่มเชิงเข้ารหัสเท่านั้น (ชนกัน = เอกสารสองใบถูกมองเป็นใบเดียว) */
export function newIdempotencyKey(): string {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    throw new Error('crypto.randomUUID ไม่พร้อมใช้งาน — ไม่สร้าง Idempotency-Key จากแหล่งที่เดาได้');
  }
  return crypto.randomUUID();
}
